import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import jsPDF from "jspdf";
import { saveAs } from "file-saver";
import Papa from "papaparse";

interface ExportAnalyticsProps {
  data: {
    productivityScore: number;
    totalTasks: number;
    completedTasks: number;
    eisenhowerMatrix: {
      urgent_important: number;
      not_urgent_important: number;
      urgent_not_important: number;
      not_urgent_not_important: number;
    };
    tasksByPriority: {
      critical: number;
      important: number;
      normal: number;
    };
  };
}

export function ExportAnalytics({ data }: ExportAnalyticsProps) {
  const exportAsPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(20);
    doc.text("Analytics Report", 20, 20);
    
    // Add date
    doc.setFontSize(12);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
    
    // Add productivity metrics
    doc.setFontSize(16);
    doc.text("Productivity Metrics", 20, 45);
    doc.setFontSize(12);
    doc.text(`Productivity Score: ${data.productivityScore}%`, 25, 55);
    doc.text(`Total Tasks: ${data.totalTasks}`, 25, 65);
    doc.text(`Completed Tasks: ${data.completedTasks}`, 25, 75);
    
    // Add Eisenhower Matrix data
    doc.setFontSize(16);
    doc.text("Eisenhower Matrix", 20, 90);
    doc.setFontSize(12);
    doc.text(`Urgent & Important: ${data.eisenhowerMatrix.urgent_important}`, 25, 100);
    doc.text(`Not Urgent but Important: ${data.eisenhowerMatrix.not_urgent_important}`, 25, 110);
    doc.text(`Urgent but Not Important: ${data.eisenhowerMatrix.urgent_not_important}`, 25, 120);
    doc.text(`Not Urgent & Not Important: ${data.eisenhowerMatrix.not_urgent_not_important}`, 25, 130);
    
    // Add Task Priority data
    doc.setFontSize(16);
    doc.text("Tasks by Priority", 20, 145);
    doc.setFontSize(12);
    doc.text(`Critical: ${data.tasksByPriority.critical}`, 25, 155);
    doc.text(`Important: ${data.tasksByPriority.important}`, 25, 165);
    doc.text(`Normal: ${data.tasksByPriority.normal}`, 25, 175);
    
    // Save the PDF
    doc.save("analytics-report.pdf");
  };

  const exportAsCSV = () => {
    const csvData = [
      {
        category: "Productivity",
        metric: "Productivity Score",
        value: data.productivityScore
      },
      {
        category: "Productivity",
        metric: "Total Tasks",
        value: data.totalTasks
      },
      {
        category: "Productivity",
        metric: "Completed Tasks",
        value: data.completedTasks
      },
      {
        category: "Eisenhower Matrix",
        metric: "Urgent & Important",
        value: data.eisenhowerMatrix.urgent_important
      },
      {
        category: "Eisenhower Matrix",
        metric: "Not Urgent but Important",
        value: data.eisenhowerMatrix.not_urgent_important
      },
      {
        category: "Eisenhower Matrix",
        metric: "Urgent but Not Important",
        value: data.eisenhowerMatrix.urgent_not_important
      },
      {
        category: "Eisenhower Matrix",
        metric: "Not Urgent & Not Important",
        value: data.eisenhowerMatrix.not_urgent_not_important
      },
      {
        category: "Task Priority",
        metric: "Critical",
        value: data.tasksByPriority.critical
      },
      {
        category: "Task Priority",
        metric: "Important",
        value: data.tasksByPriority.important
      },
      {
        category: "Task Priority",
        metric: "Normal",
        value: data.tasksByPriority.normal
      }
    ];

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, "analytics-report.csv");
  };

  const exportAsJSON = () => {
    const jsonData = {
      generatedAt: new Date().toISOString(),
      metrics: {
        productivity: {
          score: data.productivityScore,
          totalTasks: data.totalTasks,
          completedTasks: data.completedTasks
        },
        eisenhowerMatrix: data.eisenhowerMatrix,
        tasksByPriority: data.tasksByPriority
      }
    };

    const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
      type: "application/json;charset=utf-8"
    });
    saveAs(blob, "analytics-report.json");
  };

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
      <Button
        variant="outline"
        size="sm"
        onClick={exportAsPDF}
        className="flex items-center gap-2 text-black bg-white hover:bg-gray-100"
      >
        <Download className="h-4 w-4" />
        Export as PDF
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={exportAsCSV}
        className="flex items-center gap-2 text-black bg-white hover:bg-gray-100"
      >
        <Download className="h-4 w-4" />
        Export as CSV
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={exportAsJSON}
        className="flex items-center gap-2 text-black bg-white hover:bg-gray-100"
      >
        <Download className="h-4 w-4" />
        Export as JSON
      </Button>
    </div>
  );
} 